/**
 * Billiards Game Persistence Service
 *
 * Auto-saves game state to local storage after each shot.
 */

import {
  BilliardsGameState,
  BilliardsGameSettings,
  BilliardsDifficulty,
} from '../types';

export interface SavedBilliardsGame {
  gameId: string;
  mode: BilliardsGameSettings['mode'];
  gameState: BilliardsGameState;
  metadata: {
    aiDifficulty?: BilliardsDifficulty;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class BilliardsPersistence {
  private readonly STORAGE_KEY = 'billiards_saved_games';
  private readonly AUTO_SAVE_KEY = 'billiards_auto_save';

  /** Auto-save the current game state after each shot. */
  autoSave(
    mode: BilliardsGameSettings['mode'],
    gameState: BilliardsGameState,
    metadata: SavedBilliardsGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedBilliardsGame = {
        gameId: 'auto_save',
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch {
      return false;
    }
  }

  /** Load the auto-saved game state. */
  loadAutoSave(): SavedBilliardsGame | null {
    try {
      const stored = localStorage.getItem(this.AUTO_SAVE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore parse errors
    }
    return null;
  }

  /** Clear the auto-save. */
  clearAutoSave(): void {
    localStorage.removeItem(this.AUTO_SAVE_KEY);
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return localStorage.getItem(this.AUTO_SAVE_KEY) !== null;
  }

  /** Save a completed game for history. */
  saveCompletedGame(game: SavedBilliardsGame): boolean {
    try {
      const history = this.getGameHistory();
      history.push(game);
      // Keep only last 20 games
      const trimmed = history.slice(-20);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmed));
      return true;
    } catch {
      return false;
    }
  }

  /** Get game history. */
  getGameHistory(): SavedBilliardsGame[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore parse errors
    }
    return [];
  }

  /** Clear all saved games. */
  clearAll(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.AUTO_SAVE_KEY);
  }
}
